import { AnimatePresence, motion } from 'framer-motion';
import { Bell, ChevronDown, Menu, Search, User } from 'lucide-react';
import React, { useState } from 'react';

const Header = ({ toggleSidebar }) => {
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  
  const dropdownVariants = {
    hidden: { 
      opacity: 0, 
      y: -20,
      scale: 0.95
    },
    visible: { 
      opacity: 1, 
      y: 0,
      scale: 1,
      transition: {
        type: "spring",
        duration: 0.3,
        damping: 25,
        stiffness: 500
      }
    },
    exit: { 
      opacity: 0, 
      y: -20,
      scale: 0.95,
      transition: {
        duration: 0.2
      }
    }
  };
  
  // Dummy notifications
  const notifications = [
    {
      id: 1,
      message: 'New application for Frontend Developer position',
      time: '10 minutes ago',
      read: false
    },
    {
      id: 2,
      message: 'Interview scheduled with John Doe',
      time: '1 hour ago',
      read: false
    },
    {
      id: 3,
      message: 'New message from Sarah regarding the UX position',
      time: '3 hours ago',
      read: true
    },
    {
      id: 4,
      message: 'Candidate review completed by team lead',
      time: '1 day ago',
      read: true
    }
  ];

  return (
    <header className="h-16 bg-white border-b border-gray-100 shadow-soft">
      <div className="flex items-center justify-between h-full px-6">
        {/* Left section: Mobile menu button */}
        <div className="flex items-center">
          <button 
            onClick={toggleSidebar}
            className="block lg:hidden p-2 mr-2 text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-100"
          >
            <Menu size={20} />
          </button>
          
          {/* Search */}
          <div className="hidden md:flex items-center ml-4">
            <div className="relative">
              <input 
                type="text" 
                placeholder="Search..." 
                className="pl-10 pr-4 py-2 w-64 text-sm rounded-lg border border-gray-200 focus:border-primary-500 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
              />
              <Search size={16} className="absolute left-3 top-2.5 text-gray-400" />
            </div>
          </div>
        </div>
        
        {/* Right section: Notifications, help, profile */}
        <div className="flex items-center space-x-4">
          {/* Notifications */}
          <div className="relative">
            <button 
              className="p-2 text-gray-600 hover:text-gray-900 rounded-full hover:bg-gray-100 relative"
              onClick={() => setShowNotifications(!showNotifications)}
            >
              <Bell size={20} />
              {notifications.some(n => !n.read) && (
                <span className="absolute top-1 right-1 w-2.5 h-2.5 bg-secondary-500 rounded-full border-2 border-white"></span>
              )}
            </button>
            
            <AnimatePresence>
              {showNotifications && (
                <motion.div 
                  className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-elegant border border-gray-100 z-30"
                  variants={dropdownVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="p-4 border-b border-gray-100">
                    <h3 className="text-sm font-semibold text-gray-900 font-heading">Notifications</h3>
                  </div>
                  
                  <div className="max-h-96 overflow-y-auto">
                    {notifications.map(notification => (
                      <div 
                        key={notification.id}
                        className={`p-4 border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors cursor-pointer flex items-start ${
                          notification.read ? 'opacity-70' : ''
                        }`}
                      >
                        <div className="flex-shrink-0 mt-0.5">
                          {!notification.read && (
                            <span className="block w-2 h-2 bg-primary-500 rounded-full"></span>
                          )}
                        </div>
                        <div className="ml-3 flex-1">
                          <p className="text-sm text-gray-800">{notification.message}</p>
                          <p className="text-xs text-gray-500 mt-1">{notification.time}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <div className="p-3 text-center border-t border-gray-100">
                    <button className="text-sm text-primary-600 font-medium hover:text-primary-700 transition-colors">
                      Mark all as read
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          
          {/* Profile */}
          <div className="relative">
            <button 
              className="flex items-center space-x-2 focus:outline-none"
              onClick={() => setShowProfileMenu(!showProfileMenu)}
            >
              <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center overflow-hidden border-2 border-white">
                <User size={18} className="text-primary-600" />
              </div>
              <div className="hidden md:block text-left">
                <p className="text-sm font-medium text-gray-800">Company Admin</p>
                <p className="text-xs text-gray-500">Tech Innovations Inc.</p>
              </div>
              <ChevronDown size={16} className="hidden md:block text-gray-400" />
            </button>
            
            <AnimatePresence>
              {showProfileMenu && (
                <motion.div 
                  className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-elegant border border-gray-100 z-30"
                  variants={dropdownVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="pt-4 pb-3 px-4 border-b border-gray-100">
                    <p className="text-sm font-medium text-gray-900">Company Admin</p>
                    <p className="text-xs text-gray-500 mt-1">admin@techinnovations.com</p>
                  </div>
                  
                  <div className="py-2">
                    <button className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                      Profile Settings
                    </button>
                    <button className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                      Company Settings
                    </button>
                    <button className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                      Billing
                    </button>
                  </div>
                  
                  <div className="py-2 border-t border-gray-100">
                    <button className="w-full text-left px-4 py-2 text-sm text-secondary-600 hover:bg-gray-50 transition-colors">
                      Sign out
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header; 